import React, { useState, useEffect, useCallback } from "react";
import API from "../../utils/api";
import toast from "react-hot-toast";

const CATEGORIES = [
  { key: "introduction",     label: "Introduction / First Contact" },
  { key: "demo_followup",    label: "Demo / Visit Follow-up" },
  { key: "clinical_services",label: "Clinical Services" },
  { key: "proposal",         label: "Proposal" },
  { key: "followup_reminder",label: "Follow-up Reminder" },
  { key: "reengagement",     label: "Re-engagement" },
  { key: "thankyou",         label: "Thank You / Closing" },
  { key: "event_announce",   label: "Event / Announcement" },
  { key: "payment_reminder", label: "Payment Reminder" },
  { key: "custom",           label: "Custom" },
];

const VARS = ["{{contact_name}}", "{{institution_name}}", "{{sender_name}}", "{{company_name}}"];

const EMPTY_FORM = {
  type: "email", category: "introduction", company: "ebek",
  name: "", subject: "", body: "", sort_order: 0,
};

export default function MessageTemplates({ user }) {
  const accent      = user?.company === "ebekmed" ? "#2AB5A5" : "#E31E24";
  const isSuperAdmin = user?.role === "superadmin";

  const [typeTab,     setTypeTab]     = useState("email");
  const [catFilter,   setCatFilter]   = useState("");
  const [compFilter,  setCompFilter]  = useState("");
  const [search,      setSearch]      = useState("");
  const [templates,   setTemplates]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [seeding,     setSeeding]     = useState(false);

  // Form
  const [showForm,  setShowForm]  = useState(false);
  const [editId,    setEditId]    = useState(null);
  const [form,      setForm]      = useState(EMPTY_FORM);
  const [saving,    setSaving]    = useState(false);
  const [preview,   setPreview]   = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ type: typeTab });
      if (catFilter)  params.set("category", catFilter);
      if (search)     params.set("search",   search);
      const res = await API.get(`/message-templates?${params}`);
      let rows = res.data || [];
      // Superadmin sees all; others auto-filtered by server. Apply client-side company filter for superadmin.
      if (compFilter) rows = rows.filter(t => t.company === compFilter || t.company === "all");
      setTemplates(rows);
    } catch { toast.error("Failed to load templates"); }
    setLoading(false);
  }, [typeTab, catFilter, search, compFilter]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditId(null);
    setForm({ ...EMPTY_FORM, type: typeTab, company: user?.company || "ebek" });
    setPreview(false);
    setShowForm(true);
  };

  const openEdit = (t) => {
    setEditId(t.id);
    setForm({
      type:       t.type,
      category:   t.category,
      company:    t.company,
      name:       t.name,
      subject:    t.subject || "",
      body:       t.body || "",
      sort_order: t.sort_order || 0,
    });
    setPreview(false);
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim() || !form.body.trim() || !form.category)
      return toast.error("Name, category and body are required");
    setSaving(true);
    try {
      if (editId) {
        await API.put(`/message-templates/${editId}`, form);
        toast.success("Template updated");
      } else {
        await API.post("/message-templates", form);
        toast.success("Template created");
      }
      setShowForm(false);
      load();
    } catch (e) { toast.error(e.response?.data?.error || "Save failed"); }
    setSaving(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await API.delete(`/message-templates/${id}`);
      setTemplates(t => t.filter(x => x.id !== id));
      toast.success("Template deleted");
    } catch (e) { toast.error(e.response?.data?.error || "Delete failed"); }
  };

  const seedDefaults = async () => {
    setSeeding(true);
    try {
      const res = await API.post("/message-templates/seed");
      toast.success(res.data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.error || "Seed failed"); }
    setSeeding(false);
  };

  const insertVar = (v) => {
    setForm(f => ({ ...f, body: f.body + v }));
  };

  // ── Styles ──────────────────────────────────────────────────
  const card = { background: "#fff", borderRadius: 16, border: "1px solid #e2e8f0", padding: 20, marginBottom: 14 };
  const btn  = (active, extra = {}) => ({
    padding: "9px 18px", border: "none", borderRadius: 20,
    background: active ? accent : "#f1f5f9",
    color: active ? "#fff" : "#64748b",
    fontWeight: 800, fontSize: 13, cursor: "pointer", fontFamily: "inherit",
    ...extra,
  });
  const inp = {
    width: "100%", padding: "10px 12px", borderRadius: 10,
    border: "1px solid #e2e8f0", fontSize: 13, fontFamily: "inherit",
    outline: "none", boxSizing: "border-box",
  };
  const lbl = { display: "block", fontWeight: 700, fontSize: 12, color: "#475569", marginBottom: 5 };

  const catLabel = (key) => CATEGORIES.find(c => c.key === key)?.label || key;

  const compBadge = (company) => {
    const MAP = { ebek: ["#fee2e2","#b91c1c","Ebek"], ebekmed: ["#d1fae5","#065f46","EbekMed"], all: ["#e0f2fe","#0369a1","All"] };
    const [bg, color, label] = MAP[company] || ["#f1f5f9","#64748b", company];
    return <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: bg, color, marginLeft: 6 }}>{label}</span>;
  };

  return (
    <div style={{ maxWidth: 920, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ ...card, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 28 }}>📝</span>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#1e293b" }}>Message Templates</div>
            <div style={{ fontSize: 11, color: "#94a3b8" }}>Manage email and WhatsApp templates for your sales team</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {isSuperAdmin && (
            <button onClick={seedDefaults} disabled={seeding}
              style={{ ...btn(false), background: "#fff", border: `1.5px solid ${accent}`, color: accent }}>
              {seeding ? "Seeding…" : "🌱 Seed Defaults"}
            </button>
          )}
          <button onClick={openCreate} style={btn(true)}>
            + New Template
          </button>
        </div>
      </div>

      {/* Type tabs + filters */}
      <div style={{ ...card, padding: "14px 20px", display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
        {["email","whatsapp"].map(t => (
          <button key={t} style={btn(typeTab === t)} onClick={() => setTypeTab(t)}>
            {t === "email" ? "✉️ Email" : "💬 WhatsApp"}
          </button>
        ))}
        <div style={{ flex: 1 }} />
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search…"
          style={{ ...inp, width: 160, padding: "8px 12px" }} />
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)}
          style={{ padding: "8px 10px", borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12, fontFamily: "inherit", background: "#f8fafc" }}>
          <option value="">All Categories</option>
          {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        {isSuperAdmin && (
          <select value={compFilter} onChange={e => setCompFilter(e.target.value)}
            style={{ padding: "8px 10px", borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12, fontFamily: "inherit", background: "#f8fafc" }}>
            <option value="">All Companies</option>
            <option value="ebek">Ebek</option>
            <option value="ebekmed">EbekMed</option>
            <option value="all">All (shared)</option>
          </select>
        )}
      </div>

      {/* Variable hint */}
      <div style={{ marginBottom: 14, padding: "8px 14px", background: "#f0f9ff", borderRadius: 12, fontSize: 12, color: "#0369a1", border: "1px solid #bae6fd" }}>
        💡 Use these variables in templates — they are auto-filled when sending:
        {VARS.map(v => (
          <code key={v} style={{ marginLeft: 8, background: "#dbeafe", padding: "1px 6px", borderRadius: 4, fontSize: 11 }}>{v}</code>
        ))}
      </div>

      {/* Template cards */}
      {loading ? (
        <div style={{ textAlign: "center", padding: 60, color: "#94a3b8" }}>Loading…</div>
      ) : templates.length === 0 ? (
        <div style={{ ...card, textAlign: "center", padding: 48 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📝</div>
          <div style={{ fontWeight: 700, color: "#1e293b", marginBottom: 6 }}>No templates yet</div>
          <div style={{ color: "#64748b", fontSize: 13, marginBottom: 20 }}>
            {isSuperAdmin
              ? "Click \"Seed Defaults\" to add pre-built templates for both companies, or create your own."
              : "Ask your admin to create templates or seed the defaults."}
          </div>
          <button onClick={openCreate} style={btn(true)}>+ Create First Template</button>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))", gap: 12 }}>
          {templates.map(t => (
            <div key={t.id} style={{ ...card, marginBottom: 0, display: "flex", flexDirection: "column" }}>
              {/* Card header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 800, fontSize: 13, color: "#1e293b" }}>{t.name}</div>
                  {t.subject && (
                    <div style={{ fontSize: 11, color: "#0369a1", marginTop: 2, fontStyle: "italic" }}>
                      Subject: {t.subject}
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 4, marginLeft: 8 }}>
                  <button onClick={() => openEdit(t)}
                    style={{ padding: "4px 10px", background: "#f1f5f9", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", color: "#475569" }}>
                    Edit
                  </button>
                  <button onClick={() => remove(t.id)}
                    style={{ padding: "4px 10px", background: "#fee2e2", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", color: "#dc2626" }}>
                    Del
                  </button>
                </div>
              </div>
              {/* Badges */}
              <div style={{ display: "flex", gap: 5, marginBottom: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 20, background: "#f1f5f9", color: "#64748b" }}>
                  {catLabel(t.category)}
                </span>
                {compBadge(t.company)}
              </div>
              {/* Body preview */}
              <div style={{ flex: 1, fontSize: 12, color: "#64748b", whiteSpace: "pre-wrap",
                overflow: "hidden", maxHeight: 80, lineHeight: 1.5,
                maskImage: "linear-gradient(to bottom, #000 60%, transparent 100%)" }}>
                {t.body}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create / Edit Form Modal */}
      {showForm && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex",
          alignItems: "flex-start", justifyContent: "center", zIndex: 8000, padding: "16px", overflowY: "auto" }}>
          <div style={{ background: "#fff", borderRadius: 18, width: "100%", maxWidth: 640,
            padding: 24, boxShadow: "0 24px 64px rgba(0,0,0,0.18)", marginTop: 20 }}>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#1e293b" }}>
                {editId ? "Edit Template" : "New Template"}
              </div>
              <button onClick={() => setShowForm(false)}
                style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "#94a3b8" }}>×</button>
            </div>

            {/* Form / Preview toggle */}
            <div style={{ display: "flex", gap: 6, marginBottom: 18 }}>
              <button onClick={() => setPreview(false)} style={btn(!preview, { padding: "7px 16px", fontSize: 12 })}>Edit</button>
              <button onClick={() => setPreview(true)} style={btn(preview, { padding: "7px 16px", fontSize: 12 })}>Preview</button>
            </div>

            {preview ? (
              /* Preview panel */
              <div style={{ background: "#f8fafc", borderRadius: 12, padding: 16 }}>
                {form.subject && (
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 10, color: "#1e293b" }}>
                    Subject: {form.subject}
                  </div>
                )}
                <div style={{ whiteSpace: "pre-wrap", fontSize: 13, lineHeight: 1.7, color: "#334155" }}>
                  {form.body}
                </div>
              </div>
            ) : (
              /* Edit form */
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                {/* Type + Company */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <div>
                    <label style={lbl}>Type *</label>
                    <select value={form.type} onChange={e => setForm(f => ({ ...f, type: e.target.value }))}
                      style={inp}>
                      <option value="email">✉️ Email</option>
                      <option value="whatsapp">💬 WhatsApp</option>
                    </select>
                  </div>
                  <div>
                    <label style={lbl}>Company *</label>
                    <select value={form.company}
                      onChange={e => setForm(f => ({ ...f, company: e.target.value }))}
                      disabled={!isSuperAdmin}
                      style={{ ...inp, background: isSuperAdmin ? "#fff" : "#f8fafc" }}>
                      {isSuperAdmin && <option value="all">All Companies (shared)</option>}
                      <option value="ebek">Ebek</option>
                      <option value="ebekmed">EbekMed</option>
                    </select>
                  </div>
                </div>

                {/* Category */}
                <div>
                  <label style={lbl}>Category *</label>
                  <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                    style={inp}>
                    {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </div>

                {/* Name */}
                <div>
                  <label style={lbl}>Template Name *</label>
                  <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. Introduction — Ebek"
                    style={inp} />
                </div>

                {/* Subject (email only) */}
                {form.type === "email" && (
                  <div>
                    <label style={lbl}>Email Subject</label>
                    <input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
                      placeholder="e.g. Introduction — {{institution_name}}"
                      style={inp} />
                  </div>
                )}

                {/* Body */}
                <div>
                  <label style={lbl}>Message Body *</label>
                  {/* Variable quick-insert chips */}
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 6 }}>
                    {VARS.map(v => (
                      <button key={v} onClick={() => insertVar(v)}
                        style={{ padding: "3px 8px", background: "#e0f2fe", color: "#0369a1",
                          border: "none", borderRadius: 6, fontSize: 11, fontWeight: 600,
                          cursor: "pointer", fontFamily: "inherit" }}>
                        + {v}
                      </button>
                    ))}
                  </div>
                  <textarea
                    value={form.body}
                    onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
                    placeholder={form.type === "email"
                      ? "Dear {{contact_name}},\n\nYour email body here…"
                      : "Hi {{contact_name}} 👋\n\nYour WhatsApp message here…"}
                    style={{ ...inp, resize: "vertical", minHeight: 200, lineHeight: 1.6 }}
                  />
                </div>

                {/* Sort order */}
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                  <label style={{ ...lbl, marginBottom: 0 }}>Sort Order:</label>
                  <input type="number" value={form.sort_order}
                    onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))}
                    style={{ ...inp, width: 80 }} />
                  <span style={{ fontSize: 11, color: "#94a3b8" }}>Lower = shown first</span>
                </div>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button onClick={() => setShowForm(false)}
                style={{ flex: 1, padding: 13, background: "#f1f5f9", border: "none", borderRadius: 12,
                  fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "inherit", color: "#64748b" }}>
                Cancel
              </button>
              <button onClick={save} disabled={saving}
                style={{ flex: 2, padding: 13, background: accent, border: "none", borderRadius: 12,
                  fontWeight: 700, fontSize: 14, cursor: saving ? "not-allowed" : "pointer",
                  fontFamily: "inherit", color: "#fff", opacity: saving ? 0.7 : 1 }}>
                {saving ? "Saving…" : editId ? "Save Changes" : "Create Template"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
